import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Flex, Spinner, useDisclosure, useToast } from '@chakra-ui/react'
import { AddIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons'
import Footer from '../../components/Footer'
import {
  CalendarEvent,
  CalendarEventPayload,
  CalendarStats,
  deleteCalendarEvent,
  EditableCalendarEvent,
  fetchCalendarEvent,
  fetchCalendarEvents,
  saveCalendarEvent,
} from '../../services/calendar'
import CalendarDayView from './CalendarDayView'
import CalendarMonthView from './CalendarMonthView'
import CalendarWeekView from './CalendarWeekView'
import CalendarListView from './CalendarListView'
import CalendarEventDetailModal from './CalendarEventDetailModal'
import CalendarEventFormModal, { FormErrors } from './CalendarEventFormModal'
import {
  addDays,
  longDateLabel,
  monthLabel,
  parseDate,
  startOfWeek,
  toDateString,
  weekLabel,
} from './calendar-layout'

/**
 * A agenda do criativo, lendo a agenda interna da Piperz.
 *
 * É a mesma tela de /my-calendar no studio — com as visões de mês, semana e
 * agenda de lá, mais a de dia — e o mesmo formulário de bloqueio/compromisso.
 * Os eventos vêm prontos de /provider/calendar/events, que os monta pelo
 * InternalCalendarService: o Google Calendar não participa mais nem da
 * visualização nem da criação.
 */

type ViewMode = 'day' | 'week' | 'month' | 'list'

const EMPTY_STATS: CalendarStats = { total: 0, ensaios: 0, bloqueios: 0, compromissos: 0 }

const InternalCalendar: FC = () => {
  const toast = useToast()
  const today = useMemo(() => new Date(), [])

  // A agenda abre no dia de hoje: é a pergunta que o criativo traz para cá — "o
  // que eu tenho agora" —, e é a única visão que cabe inteira num celular sem
  // zoom. Semana, mês e agenda ficam a um toque no seletor.
  const [viewMode, setViewMode] = useState<ViewMode>('day')
  const [year, setYear] = useState(today.getFullYear())
  const [month, setMonth] = useState(today.getMonth() + 1)
  const [weekStart, setWeekStart] = useState(toDateString(startOfWeek(today)))
  const [dayDate, setDayDate] = useState(toDateString(today))
  const [selectedDate, setSelectedDate] = useState<string | null>(null)

  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [stats, setStats] = useState<CalendarStats>(EMPTY_STATS)
  const [loading, setLoading] = useState(true)

  const [detail, setDetail] = useState<CalendarEvent | null>(null)
  const [deleting, setDeleting] = useState(false)
  const detailModal = useDisclosure()

  const [editing, setEditing] = useState<EditableCalendarEvent | null>(null)
  const [formDate, setFormDate] = useState(toDateString(today))
  const [formErrors, setFormErrors] = useState<FormErrors>({})
  const [saving, setSaving] = useState(false)
  const formModal = useDisclosure()

  const loadEvents = useCallback(async () => {
    setLoading(true)
    try {
      const range =
        viewMode === 'day'
          ? { from: dayDate, to: dayDate }
          : viewMode === 'week'
          ? { from: weekStart, to: toDateString(addDays(parseDate(weekStart), 6)) }
          : { year, month }

      const { data, meta } = await fetchCalendarEvents(range)
      setEvents(data)
      setStats(meta.stats)
    } catch {
      toast({
        title: 'Não foi possível carregar sua agenda.',
        status: 'error',
        position: 'top-right',
        duration: 6000,
        isClosable: true,
      })
    } finally {
      setLoading(false)
    }
  }, [viewMode, dayDate, weekStart, year, month, toast])

  useEffect(() => {
    loadEvents()
  }, [loadEvents])

  // ── Navegação ─────────────────────────────────────────────────────────────

  const goToToday = () => {
    setYear(today.getFullYear())
    setMonth(today.getMonth() + 1)
    setWeekStart(toDateString(startOfWeek(new Date())))
    setDayDate(toDateString(new Date()))
    setSelectedDate(toDateString(new Date()))
  }

  const step = (direction: 1 | -1) => {
    setSelectedDate(null)

    if (viewMode === 'day') {
      setDayDate(toDateString(addDays(parseDate(dayDate), direction)))
      return
    }

    if (viewMode === 'week') {
      setWeekStart(toDateString(addDays(parseDate(weekStart), 7 * direction)))
      return
    }

    const cursor = new Date(year, month - 1 + direction, 1)
    setYear(cursor.getFullYear())
    setMonth(cursor.getMonth() + 1)
  }

  const periodLabel =
    viewMode === 'day'
      ? longDateLabel(dayDate)
      : viewMode === 'week'
      ? weekLabel(parseDate(weekStart), addDays(parseDate(weekStart), 6))
      : monthLabel(year, month)

  // ── Eventos ───────────────────────────────────────────────────────────────

  const openDetail = (event: CalendarEvent) => {
    setDetail(event)
    detailModal.onOpen()
  }

  /**
   * Data que o botão do cabeçalho pré-preenche: o dia aberto na visão de dia, o
   * dia selecionado no mês, hoje no resto. Criar um bloqueio olhando para a
   * semana que vem e ver o formulário abrir em hoje é o tipo de detalhe que faz
   * o criativo salvar no dia errado.
   */
  const createDefaultDate = () =>
    viewMode === 'day' ? dayDate : selectedDate ?? toDateString(new Date())

  const openCreate = (date: string) => {
    setEditing(null)
    setFormDate(date)
    setFormErrors({})
    formModal.onOpen()
  }

  const openEdit = async (event: CalendarEvent) => {
    try {
      const editable = await fetchCalendarEvent(event.id)
      setEditing(editable)
      setFormDate(editable.date)
      setFormErrors({})
      detailModal.onClose()
      formModal.onOpen()
    } catch {
      toast({
        title: 'Não foi possível abrir este evento para edição.',
        status: 'error',
        position: 'top-right',
        duration: 6000,
        isClosable: true,
      })
    }
  }

  const submit = async (payload: CalendarEventPayload, id?: number | null) => {
    setSaving(true)
    setFormErrors({})
    try {
      const data = await saveCalendarEvent(payload, id)
      formModal.onClose()
      toast({
        title: data?.message ?? 'Bloqueio salvo.',
        status: 'success',
        position: 'top-right',
        duration: 6000,
        isClosable: true,
      })
      await loadEvents()
    } catch (reason: any) {
      const response = reason?.response

      if (response?.status === 422) {
        setFormErrors(response.data?.errors ?? {})
        toast({
          title: 'Verifique os campos do formulário.',
          status: 'warning',
          position: 'top-right',
          duration: 6000,
          isClosable: true,
        })
      } else {
        toast({
          title: 'Não foi possível salvar o bloqueio.',
          description: response?.data?.message,
          status: 'error',
          position: 'top-right',
          duration: 6000,
          isClosable: true,
        })
      }
    } finally {
      setSaving(false)
    }
  }

  const remove = async (event: CalendarEvent) => {
    if (!window.confirm('Remover este evento da sua agenda?')) {
      return
    }

    setDeleting(true)
    try {
      await deleteCalendarEvent(event.id)
      detailModal.onClose()
      setDetail(null)
      toast({
        title: 'Bloqueio removido.',
        status: 'success',
        position: 'top-right',
        duration: 6000,
        isClosable: true,
      })
      await loadEvents()
    } catch {
      toast({
        title: 'Não foi possível remover o bloqueio.',
        status: 'error',
        position: 'top-right',
        duration: 6000,
        isClosable: true,
      })
    } finally {
      setDeleting(false)
    }
  }

  const summaryPeriod = viewMode === 'day' ? 'no dia' : viewMode === 'week' ? 'na semana' : 'no mês'
  const cards = [
    { label: `Ensaios ${summaryPeriod}`, value: stats.ensaios, dot: 'bg-green-500' },
    { label: 'Bloqueios', value: stats.bloqueios, dot: 'bg-red-500' },
    { label: 'Compromissos', value: stats.compromissos, dot: 'bg-yellow-400' },
    { label: 'Total de eventos', value: stats.total, dot: 'bg-slate-400' },
  ]

  return (
    <Flex justifyContent="center" mb="80px" mt="30px" p="4">
      <div className="container">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-800">Minha Agenda</h2>
            <p className="text-xs text-slate-400">
              Gerencie seus bloqueios e compromissos. Eles definem quando você está indisponível para novos
              ensaios.
            </p>
          </div>
          <Button
            size="sm"
            colorScheme="blue"
            borderRadius="pill"
            leftIcon={<AddIcon boxSize={2.5} />}
            onClick={() => openCreate(createDefaultDate())}
          >
            Novo bloqueio
          </Button>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {cards.map(card => (
            <div key={card.label} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-2">
                <span className={`h-2.5 w-2.5 rounded-full ${card.dot}`} />
                <p className="text-xs font-medium text-slate-400">{card.label}</p>
              </div>
              <p className="mt-1 text-2xl font-semibold text-slate-700">{card.value}</p>
            </div>
          ))}
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" borderRadius="pill" onClick={goToToday}>
              Hoje
            </Button>
            <Button
              size="sm"
              variant="ghost"
              borderRadius="circular"
              aria-label="Período anterior"
              onClick={() => step(-1)}
            >
              <ChevronLeftIcon boxSize={5} />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              borderRadius="circular"
              aria-label="Próximo período"
              onClick={() => step(1)}
            >
              <ChevronRightIcon boxSize={5} />
            </Button>
            <h3 className="ml-1 text-base font-medium capitalize text-slate-700 sm:text-lg">{periodLabel}</h3>
          </div>

          <div className="inline-flex rounded-full border border-slate-200 bg-slate-50 p-0.5">
            {(
              [
                ['day', 'Dia'],
                ['week', 'Semana'],
                ['month', 'Mês'],
                ['list', 'Agenda'],
              ] as [ViewMode, string][]
            ).map(([mode, label]) => (
              <button
                key={mode}
                type="button"
                onClick={() => {
                  setSelectedDate(null)
                  setViewMode(mode)
                }}
                className={`rounded-full px-3 py-1.5 text-sm font-medium transition sm:px-3.5 ${
                  viewMode === mode ? 'bg-white text-secondary-pure shadow-sm' : 'text-slate-500'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-500">
          <span className="flex items-center gap-1.5">
            <span className="h-3 w-3 rounded bg-green-500" /> Ensaio
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-3 w-3 rounded border border-dashed border-blue-500 bg-blue-100" /> Ensaio
            flexível
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-3 w-3 rounded bg-red-500" /> Bloqueio
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-3 w-3 rounded bg-yellow-400" /> Compromisso pessoal
          </span>
        </div>

        {loading ? (
          <Flex w="full" justify="center" my="60px">
            <Spinner size="xl" />
          </Flex>
        ) : viewMode === 'day' ? (
          <CalendarDayView date={dayDate} events={events} onCreate={openCreate} onOpenEvent={openDetail} />
        ) : viewMode === 'month' ? (
          <CalendarMonthView
            year={year}
            month={month}
            events={events}
            selectedDate={selectedDate}
            onSelectDate={date => setSelectedDate(current => (current === date ? null : date))}
            onCreate={openCreate}
            onOpenEvent={openDetail}
          />
        ) : viewMode === 'week' ? (
          <CalendarWeekView
            weekStart={weekStart}
            events={events}
            onCreate={openCreate}
            onOpenEvent={openDetail}
          />
        ) : (
          <CalendarListView events={events} periodLabel={periodLabel} onOpenEvent={openDetail} />
        )}

        <CalendarEventDetailModal
          event={detail}
          isOpen={detailModal.isOpen}
          isDeleting={deleting}
          onClose={detailModal.onClose}
          onEdit={openEdit}
          onDelete={remove}
          onSessionAdjusted={loadEvents}
        />

        <CalendarEventFormModal
          isOpen={formModal.isOpen}
          isSaving={saving}
          event={editing}
          date={formDate}
          errors={formErrors}
          onClose={formModal.onClose}
          onSubmit={submit}
        />
      </div>
      <Footer />
    </Flex>
  )
}

export default InternalCalendar
